import { Link, isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { useT } from '@/shared/i18n';
import { WorkerizeWordmark } from '@/shared/ui/brand';
import { ROUTES } from './route-paths';
import type { ReactNode } from 'react';

/**
 * RouteErrorBoundary — the router-level `errorElement` (DEC-051 polish).
 *
 * Replaces React Router's default developer "Unexpected Application Error"
 * screen with a clean, Workerize-branded, bilingual fallback. A genuine 404
 * (no matched route) shows the not-found copy; anything else shows the generic
 * error copy. No stack traces are exposed to users. Offers a safe "Back home"
 * link plus a "Try again" reload. Rendered inside the providers, so theme +
 * i18n are available.
 */
export function RouteErrorBoundary(): ReactNode {
  const error = useRouteError();
  const { t } = useT();
  const isNotFound = isRouteErrorResponse(error) && error.status === 404;

  const title = isNotFound ? t('router.not-found.title') : t('router.error.title');
  const body = isNotFound ? t('router.not-found.body') : t('router.error.body');

  return (
    <main
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center overflow-hidden bg-surface-base px-margin-mobile py-16 text-center"
    >
      <div
        aria-hidden="true"
        className="dotted-grid pointer-events-none absolute inset-0"
      />
      <div className="relative z-10 mx-auto w-full max-w-md">
        <WorkerizeWordmark size={30} className="mx-auto mb-10" />
        <h1 className="mb-3 text-3xl font-bold tracking-tight text-on-surface">{title}</h1>
        <p className="mb-8 leading-relaxed text-on-surface-variant">{body}</p>
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to={ROUTES.landing}
            className="w-full rounded-lg bg-secondary-fixed px-8 py-3 text-sm font-semibold text-on-secondary-fixed transition-transform hover:scale-105 active:scale-95 sm:w-auto"
          >
            {t('router.error.home')}
          </Link>
          {!isNotFound ? (
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="w-full rounded-lg border border-outline/20 bg-surface-container-lowest px-8 py-3 text-sm font-semibold text-on-surface transition-colors hover:bg-surface-container-low sm:w-auto"
            >
              {t('router.error.retry')}
            </button>
          ) : null}
        </div>
      </div>
    </main>
  );
}

export default RouteErrorBoundary;
